/**
 * CNPJ lookup via BrasilAPI (free, public, no API key). Wraps the open Receita
 * Federal dataset. Used by the onboarding wizard to auto-fill a new business
 * from its CNPJ.
 *
 * Docs: https://brasilapi.com.br/docs#tag/CNPJ
 */

const BRASILAPI_CNPJ = 'https://brasilapi.com.br/api/cnpj/v1';

/** Normalized company data we actually use in the app. */
export interface CnpjLookupResult {
    /** Formatted CNPJ "00.000.000/0000-00". */
    cnpj: string;
    /** Public-facing name — nome fantasia when present, else razão social. */
    name: string;
    /** Razão social (legal company name). */
    legalName: string;
    /** Email registered at Receita (often the accountant's; may be empty). */
    email: string;
    /** Phone registered at Receita (formatted; may be empty). */
    phone: string;
    /** Primary activity (CNAE description). */
    cnae: string;
    city: string;
    state: string;
    /** Composed street line "Rua, número - bairro" (for display in the result card). */
    address1: string;
    /** Structured address parts (for prefilling the separate form fields). */
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    zipCode: string;
    /** Receita situation, e.g. "ATIVA" / "BAIXADA". */
    situation: string;
    /** True when the company is active at Receita. */
    active: boolean;
    /** Raw Receita payload (JSON string) — persisted as the CNPJ snapshot. */
    snapshot: string;
}

/** Strip every non-digit. */
export function onlyDigits(value: string): string {
    return (value || '').replace(/\D+/g, '');
}

/** Keep only CNPJ characters (A–Z + 0–9), uppercased, max 14.
 *  From July 2026 the CNPJ is alphanumeric: 12 alphanumeric positions + 2 numeric
 *  check digits. Old all-numeric CNPJs are a subset, so this works for both. */
export function cleanCnpj(value: string): string {
    return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 14);
}

/** Format 14 chars as "00.000.000/0000-00" / "12.ABC.345/01DE-35" (unchanged if not 14). */
export function formatCnpj(value: string): string {
    const d = cleanCnpj(value);
    if (d.length !== 14) return value;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Format a raw CEP "01310100" as "01310-100". */
function formatCep(cep?: string | null): string {
    const d = onlyDigits(cep || '');
    if (d.length !== 8) return cep || '';
    return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** Format a raw phone "1123851939" as "(11) 23851939". */
function formatPhone(raw?: string | null): string {
    const d = onlyDigits(raw || '');
    if (d.length < 10) return raw || '';
    return `(${d.slice(0, 2)}) ${d.slice(2)}`;
}

// Mod-11 weights for the two CNPJ check digits.
const CNPJ_WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Validate the CNPJ check digits (offline — confirms it's well-formed, NOT that
 * the company exists). Supports the alphanumeric CNPJ (2026): the 12 base
 * positions may be A–Z/0–9 and contribute (ASCII − 48) to the sum; the 2 check
 * digits are numeric. Backward-compatible with all-numeric CNPJs.
 */
export function isValidCnpj(value: string): boolean {
    const c = cleanCnpj(value);
    if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(c)) return false;
    if (/^(.)\1{13}$/.test(c)) return false; // reject repeated single char
    const dv = (weights: number[]): number => {
        let sum = 0;
        for (let i = 0; i < weights.length; i++) {
            sum += (c.charCodeAt(i) - 48) * weights[i];
        }
        const r = sum % 11;
        return r < 2 ? 0 : 11 - r;
    };
    return dv(CNPJ_WEIGHTS_DV1) === Number(c[12]) && dv(CNPJ_WEIGHTS_DV2) === Number(c[13]);
}

/** Raised when the CNPJ is well-formed but Receita has no record of it. */
export class CnpjNotFoundError extends Error {}

/**
 * Look up a CNPJ. Throws {@link CnpjNotFoundError} on 404, or a generic Error
 * on network/other failures so the UI can show the right message.
 */
export async function lookupCnpj(cnpj: string): Promise<CnpjLookupResult> {
    const digits = cleanCnpj(cnpj);
    const res = await fetch(`${BRASILAPI_CNPJ}/${digits}`, {
        headers: { Accept: 'application/json' },
    });

    if (res.status === 404) {
        throw new CnpjNotFoundError('CNPJ not found');
    }
    if (!res.ok) {
        throw new Error(`CNPJ lookup failed (${res.status})`);
    }

    const body = await res.text();
    const d = JSON.parse(body);

    const legalName: string = (d.razao_social || '').trim();
    const fantasy: string = (d.nome_fantasia || '').trim();
    const street = (d.logradouro || '').trim();
    const number = (d.numero || '').trim();
    const complement = (d.complemento || '').trim();
    const neighborhood = (d.bairro || '').trim();
    const streetLine = [street, number].filter(Boolean).join(', ');
    const address1 = [streetLine, neighborhood].filter(Boolean).join(' - ');

    return {
        cnpj: formatCnpj(digits),
        name: fantasy || legalName,
        legalName,
        email: (d.email || '').trim(),
        phone: formatPhone(d.ddd_telefone_1),
        cnae: (d.cnae_fiscal_descricao || '').trim(),
        city: (d.municipio || '').trim(),
        state: (d.uf || '').trim(),
        address1,
        street,
        number,
        complement,
        neighborhood,
        zipCode: formatCep(d.cep),
        situation: (d.descricao_situacao_cadastral || '').trim(),
        active: (d.descricao_situacao_cadastral || '').toUpperCase() === 'ATIVA',
        snapshot: body,
    };
}
